import { FC, useEffect, useLayoutEffect, useState, useCallback } from "react";
import "../../assets/components/Table/Table_header.scss";
import "./LicenseKeys.scss";
import { Products, UsersT } from "../../models/models";
import {
    useGetListLicenseQuery,
    useDeleteLicenseMutation,
    useGetUsersListQuery,
    useGetProductsListQuery,
} from "../../redux/RTKQuery/api";
import { useAppDispatch, useAppSelector } from "../../assets/hooks/hooks";
import { Loading, uiSlicedActions } from "../../redux/ui-slice";
import Loader from "../../assets/components/Loader/Loader";
import { LicenseList, TotalLicenses } from "./model";
import { licenseSliceActions } from "../../redux/license-slice";
import { Page } from "../../assets/components/PaginationBar/PaginationBar";
import PageTitle from "../../assets/components/PageTitle/PageTitle";
import LicenseKeysTableHeader from "./LicenseKeysTable/LicenseKeysTableHeader";
import LicenseKeysTable from "./LicenseKeysTable/LicenseKeysTable";
import LicenseKeysSearchBar from "./LicenseKeysTable/LicenseKeysSearchBar";
import { SearchBarDataHandler } from "../../assets/components/SearchBar/SearchBar";
import { toast } from "react-toastify";

const LicenseKeys: FC = () => {
    const dispatch = useAppDispatch();
    const loading: Loading = useAppSelector((state) => state.ui.loading);

    const pageSize: number = 30;
    const [currentSizePage, setCurrentSizePage] = useState(pageSize);
    const [pageNumber, setPageNumber] = useState<Page>(1);
    const [dataTable, setData] = useState<LicenseList>([]);
    const [users, setDataUsers] = useState<UsersT>([]);
    const [products, setDataProducts] = useState<Products>([]);
    const [totalLicenses, setTotalLicenses] = useState<TotalLicenses>(0);
    const [quiresData, setQuires] = useState<SearchBarDataHandler>({});

    const [
        deleteLicense,
        {
            isSuccess: isSuccessDeleteLicense,
            isLoading: isLoadingDeleteLicense,
            isError: isErrorDeleteLicense,
            reset,
        },
    ] = useDeleteLicenseMutation({
        fixedCacheKey: "delete_actual_license",
    });

    const {
        searchInput: _searchInput,
        searchByOrderId: _searchByOrderId,
        userId: _legacyUserId,
        productId: _legacyProductId,
        userIds,
        productIds,
        ...restQuires
    } = quiresData;

    const {
        data: dataLicenses,
        isSuccess: isSuccessLicenses,
        isError: isErrorLicenses,
        isFetching: isFetchingLicenses,
    } = useGetListLicenseQuery(
        {
            pageNumber,
            pageSize,
            licenseKey: quiresData.searchInput,
            orderId: quiresData.searchByOrderId,
            userId: userIds,
            productId: productIds,
            ...restQuires,
        },
        {
            refetchOnFocus: true,
        }
    );

    // Set page number to 1 when filters change
    useEffect(() => {
        setPageNumber(1);
    }, [quiresData]);

    const {
        data: dataUsers,
        isLoading: isLoadingUsers,
        isSuccess: isSuccessUsers,
        isError: isErrorUsers,
    } = useGetUsersListQuery("");

    const {
        data: dataProducts,
        isLoading: isLoadingProducts,
        isSuccess: isSuccessProducts,
        isError: isErrorProducts,
    } = useGetProductsListQuery({});

    const handleLicensesData = useCallback(() => {
        if (isSuccessLicenses && dataLicenses) {
            setData(dataLicenses.detailedInfo);
            if (dataLicenses.filteredLicensesCount !== undefined) {
                setCurrentSizePage(() => {
                    if (totalLicenses < pageSize) {
                        return dataLicenses.filteredLicensesCount as number;
                    } else {
                        return pageSize;
                    }
                });
                setTotalLicenses(dataLicenses.filteredLicensesCount);
            } else {
                setTotalLicenses(dataLicenses.totalLicenses);
            }
        }
        if (isErrorLicenses) {
            toast.error("Error loading license keys");
        }
    }, [
        isSuccessLicenses,
        dataLicenses,
        totalLicenses,
        pageSize,
        isErrorLicenses,
    ]);

    const handleUsersData = useCallback(() => {
        if (isSuccessUsers && dataUsers) {
            setDataUsers(dataUsers);
        }
        if (isErrorUsers) {
            toast.error("Error loading users");
        }
    }, [isSuccessUsers, dataUsers, isErrorUsers]);

    const handleProductsData = useCallback(() => {
        if (isSuccessProducts && dataProducts) {
            setDataProducts(dataProducts.products);
        }
        if (isErrorProducts) {
            toast.error("Error loading products");
        }
    }, [isSuccessProducts, dataProducts, isErrorProducts]);

    const handlerDeleteLicenses = useCallback(() => {
        if (isSuccessDeleteLicense) {
            dispatch(licenseSliceActions.removeAllCandidates());
            toast.success("License key(s) successfully deleted!");
            reset();
        }
        if (isErrorDeleteLicense) {
            toast.error("License key(s) not deleted!");
            reset();
        }
    }, [dispatch, isSuccessDeleteLicense, isErrorDeleteLicense, reset]);

    const handleLoadingState = useCallback(() => {
        const loadingGlobal =
            isFetchingLicenses ||
            isLoadingUsers ||
            isLoadingProducts ||
            isLoadingDeleteLicense;
        dispatch(
            uiSlicedActions.setLoading(loadingGlobal ? "pending" : "hidden")
        );
    }, [
        isFetchingLicenses,
        isLoadingUsers,
        isLoadingProducts,
        isLoadingDeleteLicense,
        dispatch,
    ]);

    // Handle data fetching and state updates
    useLayoutEffect(() => {
        handleLicensesData();
        handleUsersData();
        handleProductsData();
        handleLoadingState();
        handlerDeleteLicenses();
    }, [
        handleLicensesData,
        handleUsersData,
        handleProductsData,
        handleLoadingState,
        handlerDeleteLicenses,
    ]);

    return (
        <div className="page_with_table">
            <Loader loading={loading} />
            <PageTitle text="Actual License Keys" />
            <LicenseKeysSearchBar
                users={users}
                products={products}
                dataHandler={setQuires}
            />
            <div className="table_scroll_wrapper">
                <LicenseKeysTableHeader
                    pageNumber={pageNumber}
                    data={dataLicenses}
                    pageSize={pageSize}
                    dataTable={dataTable}
                    totalLicenses={totalLicenses}
                    setCurrentPage={setPageNumber}
                    currentSizePage={currentSizePage}
                    deleteLicensesHandler={deleteLicense}
                />
                <LicenseKeysTable dataTable={dataTable} onSort={setQuires} />
            </div>
        </div>
    );
};

export default LicenseKeys;
